import { TileLayout } from "@progress/kendo-react-layout";
import { useState, useEffect, useRef } from "react";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Loader } from "@progress/kendo-react-indicators";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Window } from "@progress/kendo-react-dialogs";
import { useLocalization } from "@progress/kendo-react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignJustify, faChartLine, faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import EndpointHealthDetails from "./Endpoints/EndpointHealthDetails";
import GatewayHealthDetails from "./Gateways/GatewayHealthDetails";
import HeadUnitHealthDetails from "./HeadUnits/HeadUnitHealthDetails";
import { EventFilter } from "../../../Events/Utilities/EventsApiService";
import useChartStore from "../../../../Zustand/chartStore";
import { enMessages } from "../../../../messages/en-US";

interface Props {
  // Core data
  deviceHealthData: any;
  deviceFeatures: any;
  // Event filter management
  eventFilter: EventFilter;
  setEventFilter: (eventFilter: EventFilter) => void;

  // Device identifiers
  deviceSerialNumber: string;
  deviceId: string;
  deviceNotifications: any;

  // Loading state
  isLoading?: boolean;
}

// Utility function for row height
const calculateRowHeight = (totalRows: number): number => {
  const viewportHeight = window.innerHeight;
  const offsetHeight = 360;
  const totalGapHeight = (totalRows - 1) * 10;
  const availableHeight = viewportHeight - offsetHeight - totalGapHeight;
  const rowHeight = Math.floor(availableHeight / totalRows);
  return Math.max(60, Math.min(rowHeight, 900));
};

export const DeviceDetailsInfoTabs = ({
  deviceHealthData,
  eventFilter,
  setEventFilter,
  deviceSerialNumber,
  deviceId,
  deviceNotifications,
  deviceFeatures,
  isLoading = false,
}: Props) => {

  const [selectedTab, setSelectedTab] = useState(0);
  const [isDiagnostics, setIsDiagnostics] = useState(false);
  const [isDateFilterLoading, setIsDateFilterLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);
  const windowRef = useRef<any>(null);
  const localizationService = useLocalization();
  const {viewType, setViewType, setVisibleRange} = useChartStore((state) => ({
    viewType: state.viewType,
    setViewType: state.setViewType,
    setVisibleRange: state.setVisibleRange
  }));

  // Use ResizeObserver to track window size changes
  useEffect(() => {
    if (open && windowRef.current) {
      const windowElement = windowRef.current.element;
      if (windowElement) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setWindowHeight(entry.contentRect.height);
          }
        });
        
        resizeObserver.observe(windowElement);
        
        // Set initial height
        setWindowHeight(windowElement.offsetHeight);
        
        return () => {
          resizeObserver.disconnect();
        };
      }
    } else {
      setWindowHeight(null);
    }
    
    // Return undefined for cases where no cleanup is needed
    return undefined;
  }, [open]);

  useEffect(() => {
    setVisibleRange(undefined, undefined);
  }, [deviceId, deviceHealthData.deviceType, setVisibleRange]);

  const TabButtonGroup = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
    const tabs = deviceHealthData.deviceType !== 'endpoint' ? ['Timeline'] : ['Timeline', 'Diagnostics'];
    
    return (
      <ButtonGroup>
        {tabs.map((tab, idx) => (
          <Button
            key={tab}
            fillMode="solid"
            style={{
              borderRadius: "0px",
              backgroundColor: "transparent",
              borderColor: "transparent",
              borderBottom: selectedTab === idx ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
              color: selectedTab === idx ? "var(--kendo-color-primary)" : "inherit"
            }}
            onClick={() => {
              setSelectedTab(idx);
              setIsDiagnostics(idx === 1);
            }}
          >
            {tab}
          </Button>
        ))}
      </ButtonGroup>
    );
  };

  const ChartSelector = () => {
    const TooltipContentTemplate = (props: any) => {
      return (
        <div>
          {props.title}
        </div>
      );
    };

    return (
      <Tooltip position={'bottom'} anchorElement={'target'} content={(props) => <TooltipContentTemplate title={props.title} />} openDelay={2}>
        <ButtonGroup>
          <Button 
            title={localizationService.toLanguageString('custom.lineChart', enMessages.custom.lineChart)} 
            togglable={true} 
            selected={viewType === 'line' || viewType === 'userLine'} 
            onClick={() => {setViewType('userLine')}}>
              <FontAwesomeIcon title={localizationService.toLanguageString('custom.lineChart', enMessages.custom.lineChart)} icon={faChartLine}/>
          </Button>
          <Button 
            title={'Table'} 
            togglable={true} 
            selected={viewType === 'table'} 
            onClick={() => setViewType('table')}>
              <FontAwesomeIcon title={'Table'} icon={faAlignJustify}/>
          </Button>
        </ButtonGroup>
      </Tooltip>
    );
  };

  const DateRangeSelector = ({ isFullscreen = false }: { isFullscreen?: boolean }) => {
    return (
      <div className='flex items-center gap-x-2'>
        <div className="flex flex-col">
          <DatePicker
            value={eventFilter.startDate}
            onChange={handleStartDateChange}
            max={eventFilter.endDate || undefined}
            width="150px"
            disabled={isLoading}
          />
        </div>
        <span className="text-gray-400">-</span>
        <div className="flex flex-col">
          <DatePicker
            value={eventFilter.endDate}
            onChange={handleEndDateChange}
            min={eventFilter.startDate || undefined}
            max={moment().toDate()}
            width="150px"
            disabled={isLoading}
          />
        </div>
        {!isFullscreen && (
          <Button 
            size="small"
            onClick={() => setOpen(true)}
            style={{ padding: '4px 8px', minWidth: 'auto' }}
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
          </Button>
        )}
      </div>
    );
  };

  const [dimensions, setDimensions] = useState({ rowHeight: calculateRowHeight(1) });

  // Responsive: update rowHeight on resize/layout change
  useEffect(() => {
    const updateDimensions = () => {
      const totalRows = 1; // We have 1 row in our tile layout
      setDimensions({
        rowHeight: calculateRowHeight(totalRows),
      });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const handleStartDateChange = (event: any) => {
    if (event.value) {
      setIsDateFilterLoading(true);
      setEventFilter({
        ...eventFilter,
        startDate: moment(event.value).startOf('day').toDate(),
        endDate: eventFilter.endDate,
      });
      // Simulate loading time for better UX
      setTimeout(() => setIsDateFilterLoading(false), 500);
    }
  };

  const handleEndDateChange = (event: any) => {
    if (event.value) {
      setIsDateFilterLoading(true);
      setEventFilter({
        ...eventFilter,
        endDate: moment(event.value).endOf('day').toDate(),
        startDate: eventFilter.startDate,
      });
      // Simulate loading time for better UX
      setTimeout(() => setIsDateFilterLoading(false), 500);
    }
  };

  const handleWindowClose = () => {
    setOpen(false);
  };

  const renderTabContent = (isFullscreen: boolean = false) => {
    if (isLoading || isDateFilterLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader size="large" />
          </div>
        </div>
      );
    }

    if (!deviceHealthData.deviceData) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-600">No device data available</p>
          </div>
        </div>
      );
    }

    const chartHeight = isFullscreen 
      ? (windowHeight ? windowHeight - 200 : 450)
      : (dimensions.rowHeight - 135);

    return (
      <>
        {deviceHealthData.deviceType === "endpoint" ? (
          <EndpointHealthDetails
            deviceData={deviceHealthData.deviceData}
            eventFilter={eventFilter}
            setEventFilter={setEventFilter}
            deviceSerialNumber={deviceSerialNumber}
            deviceId={deviceId}
            deviceType={deviceHealthData.deviceType}
            chartParentHeight={chartHeight}
            isDiagnostics={isDiagnostics}
            deviceEventData={deviceHealthData.endpointEventData}
            deviceNotifications={deviceNotifications}
            deviceFeatures={deviceFeatures}
          />
        ) : deviceHealthData.deviceType === "gateway" ? (
          <GatewayHealthDetails 
            deviceData={deviceHealthData.deviceData}
            eventFilter={eventFilter}
            setEventFilter={setEventFilter}
            chartParentHeight={chartHeight}
            selectedEndpointId={deviceId}
            deviceInfo={deviceHealthData.deviceInfo}
            deviceNotifications={deviceNotifications}
          />
        ) : (
          <HeadUnitHealthDetails 
            deviceHealthData={deviceHealthData}
            eventFilter={eventFilter}
            setEventFilter={setEventFilter}
            chartParentHeight={chartHeight}
            selectedEndpointId={deviceId}
            deviceInfo={deviceHealthData.deviceInfo}
            deviceNotifications={deviceNotifications}
          />
        )}
      </>
    );
  };

  const renderLayout = (isFullscreen: boolean = false) => {
    return (
    <div
      style={
        isFullscreen
          ? { height: "80vh", display: "flex", flexDirection: "column" }
          : { height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }
      }
    >
      <div className={`flex ${isFullscreen ? 'justify-between mb-4' : 'flex-wrap justify-between items-center gap-y-2 mb-2'}`}>
        <TabButtonGroup isFullscreen={isFullscreen} />
        <div className='flex items-center gap-x-2'>
          {selectedTab === 1 ? (
            <>
              <ChartSelector />
              <DateRangeSelector isFullscreen={isFullscreen} />
            </>
          ) : (
            <DateRangeSelector isFullscreen={isFullscreen} />
          )}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {renderTabContent(isFullscreen)}
      </div>
    </div>
    );
  };

  const tileItems = [
    {
      header: "Device Health Details",
      body: (
        <div className="h-full flex flex-col">
          <div className="flex items-center gap-x-2 p-3 justify-between">
              <TabButtonGroup />
              <div className="flex items-center gap-x-4">
                {selectedTab === 1 ? (
                  <>
                    <ChartSelector />
                    <DateRangeSelector />
                  </>
                ) : (
                  <DateRangeSelector />
                )}
              </div>
          </div>
          <div className="flex-1 overflow-auto px-3 pb-3">
            {renderTabContent()}
          </div>
        </div>
      ),
      reorderable: false,
      col: 1,
      colSpan: 1,
      rowSpan: 1,
      row: 1
    }
  ];

  return (
    <>
      {!open && (
        <div className="h-full overflow-y-auto">
         <TileLayout
              className="dashboard-page-tile dashboard-analysis-tab-tiles tile-content-layout overflow-y-auto"
              columns={1}
              rowHeight={dimensions.rowHeight}
              gap={{ rows: 10, columns: 10 }}
              items={tileItems}
              style={{ height: '100%', overflow: 'auto' }}
            />
        </div>
      )}
      {open && (
        <Window
          ref={windowRef}
          title={`${localizationService.toLanguageString('custom.deviceHealthDetails', enMessages.custom.deviceHealthDetails)} - ${deviceSerialNumber}${deviceHealthData.deviceData?.asset ? ` (${deviceHealthData.deviceData.asset})` : ''}`}
          onClose={handleWindowClose}
          style={{ top: "5vh", left: "5vw", width: "90vw", height: "90vh" }}
          modal={true}
          className="custom-fullscreen-window"
        >
          {renderLayout(true)}
        </Window>
      )}
    </>
  );
}